<script module lang="ts">
	import { isLive, type Task } from '../types';

	/**
	 * The list view is not a fallback for a broken tank. It is a first-class second
	 * view of the same data, backed by the same store and the same actions — and the
	 * only one a keyboard or a screen reader can reach, since a canvas offers them
	 * nothing.
	 */

	export type Group = { key: string; heading: string; tasks: Task[] };

	/**
	 * Groups one date's tasks the way the tank arranges them: what is waiting, what
	 * is swimming, what is on the waterline, what is finished.
	 *
	 * Filters soft-deletes, like every derived read.
	 */
	export function groupTasks(tasks: Task[], date: string): Group[] {
		const today = tasks.filter((t) => isLive(t) && t.date === date);

		const treats = today.filter((t) => t.treatCost !== undefined && t.status === 'waiting');
		const waiting = today.filter((t) => t.treatCost === undefined && t.status === 'waiting');
		const open = today.filter((t) => t.status === 'open');
		const done = today.filter((t) => t.status === 'done');

		return [
			{ key: 'open', heading: 'Swimming', tasks: byCreated(open) },
			{ key: 'waiting', heading: 'Waiting', tasks: byCreated(waiting) },
			{ key: 'treats', heading: 'Guilty pleasures', tasks: byCreated(treats) },
			// Most recently finished first: the reverse of everything else, because a
			// long day's done list is read from the end.
			{ key: 'done', heading: 'Done', tasks: byCompleted(done) }
		].filter((group) => group.tasks.length > 0);
	}

	/** ULIDs sort by creation time, so this is also chronological. */
	function byCreated(tasks: Task[]): Task[] {
		return [...tasks].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
	}

	function byCompleted(tasks: Task[]): Task[] {
		return [...tasks].sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0));
	}

	/** Counts what a bulk action is about to affect, for the confirmation line. */
	export function describeSelection(count: number): string {
		if (count === 0) return 'Nothing selected';
		return `${count} ${count === 1 ? 'task' : 'tasks'} selected`;
	}

	/**
	 * How many pearls a bulk delete of `selected` would drop. Pearls are derived
	 * (`pearlBalance` in store/pearls.ts) from exactly this predicate — a done,
	 * non-treat task — so this must stay in step with that, not reimplement it loosely.
	 */
	export function pearlsAtStake(tasks: Task[], selected: Iterable<string>): number {
		const ids = new Set(selected);
		return tasks.filter((t) => ids.has(t.id) && t.status === 'done' && t.treatCost === undefined)
			.length;
	}
</script>

<script lang="ts">
	import { shiftDate, formatDay } from './DateHeader.svelte';
	import { describeCondition, actionsFor, reopenCosts } from './CreatureSheet.svelte';
	import { pearlBalance } from '../store/pearls';


	type Props = {
		open: boolean;
		date: string;
		tasks: Task[];
		onComplete: (id: string) => void;
		onReopen: (id: string) => void;
		onRelease: (id: string) => void;
		onClaim: (id: string) => void;
		onEdit: (task: Task) => void;
		onMove: (id: string, date: string) => void;
		onDelete: (id: string) => void;
		onClose: () => void;
	};

	const {
		open,
		date,
		tasks,
		onComplete,
		onReopen,
		onRelease,
		onClaim,
		onEdit,
		onMove,
		onDelete,
		onClose
	}: Props = $props();

	// Same balance the tank shows, so a treat that is affordable in one view is
	// affordable in the other.
	const balance = $derived(pearlBalance(tasks));

	const groups = $derived(groupTasks(tasks, date));
	let selected = $state(new Set<string>());

	// Reopening a done ordinary task removes an earned pearl (see `reopenTask` in
	// store/tasks.ts); with none left that pearl was already spent on a treat, and
	// the balance goes negative. Warned the same two-tap way as delete, rather than
	// let the debt appear with no explanation — see `reopenCosts` in CreatureSheet.
	let reopenArmed = $state(new Set<string>());

	function reopen(task: Task) {
		const costs = reopenCosts(task, balance);
		if (costs && !reopenArmed.has(task.id)) {
			reopenArmed = new Set(reopenArmed).add(task.id);
			return;
		}
		if (reopenArmed.has(task.id)) {
			const next = new Set(reopenArmed);
			next.delete(task.id);
			reopenArmed = next;
		}
		onReopen(task.id);
	}

	// Shown alongside the confirm step when it would drop a pearl from the balance
	// — the rest of the app never destroys a pearl silently.
	const atStake = $derived(pearlsAtStake(tasks, selected));
	let armed = $state(false);

	// A selection that outlived the date it was made on would move the wrong tasks.
	$effect(() => {
		date;
		selected = new Set();
		armed = false;
		reopenArmed = new Set();
	});

	function toggle(id: string) {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		selected = next;
		armed = false;
	}

	function moveSelected(days: number) {
		for (const id of selected) {
			const task = tasks.find((t) => t.id === id);
			if (task) onMove(id, shiftDate(task.date, days));
		}
		selected = new Set();
	}

	// Always a two-tap action, whatever is selected — a plain open task deleted
	// this way used to vanish on the first tap, with no confirmation and no undo,
	// unlike the tank sheet's own delete which always asks first.
	function deleteSelected() {
		if (!armed) {
			armed = true;
			return;
		}
		for (const id of selected) onDelete(id);
		selected = new Set();
		armed = false;
	}

</script>

{#if open}
	<section class="list" aria-label="Task list">
		<div class="inner">
		<header>
			<h2>{formatDay(date)}</h2>
			<button type="button" class="ghost" onclick={onClose}>Back to the tank</button>
		</header>

		{#if groups.length === 0}
			<p class="empty">Nothing planned for this day.</p>
		{/if}

		{#each groups as group (group.key)}
			<h3>{group.heading}</h3>
			<ul>
				{#each group.tasks as task (task.id)}
					{@const condition = describeCondition(task)}
					{@const affordable = task.treatCost !== undefined && balance >= task.treatCost}
					{@const actions = actionsFor(task, affordable)}
					<li>
						<label class="pick">
							<input
								type="checkbox"
								checked={selected.has(task.id)}
								onchange={() => toggle(task.id)}
							/>
							<span class="visually-hidden">Select {task.title}</span>
						</label>

						<div class="body">
							<span class="title" class:done={task.status === 'done'}>
								{#if task.priority}<span class="priority" title="Priority">🦈</span>{/if}
								{task.title}
							</span>
							{#if condition}<span class="condition">{condition}</span>{/if}
						</div>

						<!-- Same action rules as the tank sheet, from one shared function: the
						     list is a second view of the same data, not a second set of rules. -->
						<div class="row-actions">
							{#if actions.includes('claim')}
								<button type="button" onclick={() => onClaim(task.id)}>Claim</button>
							{:else if task.treatCost !== undefined && task.status === 'waiting'}
								<span class="locked">Need {task.treatCost - balance} more</span>
							{/if}
							{#if actions.includes('release')}
								<button type="button" onclick={() => onRelease(task.id)}>Let out</button>
							{/if}
							{#if actions.includes('complete')}
								<button type="button" onclick={() => onComplete(task.id)}>Done</button>
							{/if}
							{#if actions.includes('reopen')}
								{@const costs = reopenCosts(task, balance)}
								<button type="button" class="ghost" onclick={() => reopen(task)}>
									{#if costs && !reopenArmed.has(task.id)}
										Reopen — no pearl left
									{:else if costs}
										Reopen anyway
									{:else}
										Reopen
									{/if}
								</button>
							{/if}
							<button type="button" class="ghost" onclick={() => onEdit(task)}>Edit</button>
						</div>
					</li>
				{/each}
			</ul>
		{/each}

		</div>

		{#if selected.size > 0}
			<div class="bulk" role="group" aria-label="Bulk actions">
				<span>{describeSelection(selected.size)}</span>
				<button type="button" onclick={() => moveSelected(1)}>Push to tomorrow</button>
				<button type="button" onclick={() => moveSelected(-1)}>Pull to yesterday</button>
				{#if armed && atStake > 0}
					<span class="pearl-warning">
						Removes {atStake} {atStake === 1 ? 'pearl' : 'pearls'} —
					</span>
				{/if}
				<button type="button" class="danger" onclick={deleteSelected}>
					{armed ? 'Confirm delete' : 'Delete'}
				</button>
			</div>
		{/if}
	</section>
{/if}

<style>
	.list {
		position: fixed;
		inset: 0;
		/* Layer 10: full-screen view. Sheets (20) open above it. */
		z-index: 10;
		overflow-y: auto;
		padding: 1.5rem 1rem 6rem;
		padding-top: max(1.5rem, env(safe-area-inset-top));
		background: rgba(245, 252, 253, 0.97);
		backdrop-filter: blur(20px);
		color: #12303a;
	}

	/* A readable measure. Full width put the title at one edge of the screen and its
	   Edit button at the other, with a metre of nothing between them. */
	.inner {
		max-width: 44rem;
		margin: 0 auto;
	}

	header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
	}

	h2 {
		margin: 0;
		font-size: 1.15rem;
	}

	h3 {
		margin: 1.6rem 0 0.35rem;
		font-size: 0.8rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		opacity: 0.6;
	}

	ul {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	li {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding: 0.7rem 0.75rem;
		border-radius: 0.7rem;
		border-bottom: 1px solid rgba(18, 48, 58, 0.07);
		transition: background 0.12s;
	}

	li:hover {
		background: rgba(18, 48, 58, 0.04);
	}

	li:last-child {
		border-bottom: 0;
	}

	input[type='checkbox'] {
		width: 1.1rem;
		height: 1.1rem;
		accent-color: #12303a;
		cursor: pointer;
	}

	.body {
		display: grid;
		flex: 1;
		gap: 0.1rem;
		min-width: 0;
	}

	.title {
		font-size: 0.98rem;
	}

	.title.done {
		opacity: 0.5;
		text-decoration: line-through;
	}

	.priority {
		margin-right: 0.3em;
	}

	.condition {
		font-size: 0.78rem;
		opacity: 0.65;
	}

	.row-actions {
		display: flex;
		align-items: center;
		gap: 0.35rem;
	}

	.locked {
		font-size: 0.75rem;
		opacity: 0.6;
		white-space: nowrap;
	}

	button {
		padding: 0.35rem 0.7rem;
		border: 0;
		border-radius: 999px;
		background: #12303a;
		color: #fff;
		font-size: 0.8rem;
		cursor: pointer;
	}

	.ghost {
		background: rgba(18, 48, 58, 0.1);
		color: #12303a;
	}

	.danger {
		background: #a03325;
	}

	.pearl-warning {
		font-size: 0.78rem;
		color: #a03325;
		font-weight: 600;
	}

	button:focus-visible,
	input:focus-visible {
		outline: 2px solid #12303a;
		outline-offset: 2px;
	}

	.bulk {
		position: fixed;
		inset: auto 0 0 0;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 0.5rem;
		padding: 0.85rem 1rem calc(0.85rem + env(safe-area-inset-bottom));
		background: rgba(255, 255, 255, 0.95);
		box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
		font-size: 0.85rem;
	}

	.empty {
		margin-top: 2rem;
		opacity: 0.6;
	}

	.visually-hidden {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip-path: inset(50%);
		white-space: nowrap;
	}
</style>
